export const STREAM_TEXT_BATCH_INTERVAL_MS = 48;
export const STREAM_TEXT_SENTENCE_PAUSE_MS = 150;
export const STREAM_SECTION_GAP_MS = 220;

const SENTENCE_END_RE = /[.!?]["')\]]?\s*$/;
const BLANK_LINE_RE = /\n\s*\n/;
const DATE_COLUMN_RE = /(^|_|\s)(date|day|month|year|time|timestamp|created|updated|period)($|_|\s)/i;
const PERCENT_COLUMN_RE = /(^|_|\s)(percent|percentage|pct|rate|ratio|margin|growth|irr|roa|roi)($|_|\s)/i;
const MONEY_COLUMN_RE = /(^|_|\s)(amount|revenue|sales|cost|price|value|income|profit|loss|disbursement|outstanding|emi|loan|portfolio|collected|due|balance|ticket|size)($|_|\s)/i;

const splitSqlResponseContent = (rawContent) => {
  const text = String(rawContent || "");
  const lines = text.split("\n");
  const tableStartIndex = lines.findIndex((line) => line.trim().startsWith("|"));
  const hasSplittableTable = tableStartIndex > 0;
  const summaryLines = hasSplittableTable ? lines.slice(0, tableStartIndex) : lines;

  let nonEmptyCount = 0;
  let cutoffIndex = summaryLines.length;

  for (let index = 0; index < summaryLines.length; index += 1) {
    if (summaryLines[index].trim() !== "") {
      nonEmptyCount += 1;
      if (nonEmptyCount === 2) {
        cutoffIndex = index + 1;
        break;
      }
    }
  }

  return {
    summaryAbove: summaryLines.slice(0, cutoffIndex).join("\n").trim(),
    summaryBelow: summaryLines.slice(cutoffIndex).join("\n").trim(),
    tableContent: hasSplittableTable
      ? lines.slice(tableStartIndex).join("\n").trim()
      : "",
  };
};

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const getStructuredTableColumns = (rows) => {
  const columns = [];
  const seen = new Set();

  rows.forEach((row) => {
    if (!isPlainObject(row)) return;
    Object.keys(row).forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      columns.push(key);
    });
  });

  return columns;
};

const formatHeaderLabel = (key) =>
  String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const formatCompactNumber = (value, column = "") => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value ?? "");

  const abs = Math.abs(number);
  const decimals = abs !== 0 && abs < 1 ? 4 : abs < 100 ? 2 : 1;
  const trim = (num) =>
    Number(num.toFixed(decimals)).toLocaleString("en-IN", {
      maximumFractionDigits: decimals,
    });

  if (MONEY_COLUMN_RE.test(column)) {
    if (abs >= 1e9) return `${trim(number / 1e9)} B`;
    if (abs >= 1e6) return `${trim(number / 1e6)} M`;
    if (abs >= 1e7) return `${trim(number / 1e7)} Cr`;
    if (abs >= 1e5) return `${trim(number / 1e5)} L`;
  }

  if (abs >= 1e7) return `${trim(number / 1e7)} Cr`;
  if (abs >= 1e5) return `${trim(number / 1e5)} L`;

  return number.toLocaleString("en-IN", {
    maximumFractionDigits: abs !== 0 && abs < 100 ? 2 : 0,
  });
};

const formatDateValue = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const text = String(value || "");
  if (/^\d{4}$/.test(text)) return null;

  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const RAW_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]*)?$|^\d{4}\/\d{2}\/\d{2}$/;

const formatStructuredValue = (value, column = "") => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (DATE_COLUMN_RE.test(column) && (typeof value === "number" || RAW_DATE_RE.test(String(value)))) {
    const formattedDate = formatDateValue(value);
    if (formattedDate) return formattedDate;
  }

  if (typeof value === "string") return String(value);

  if (typeof value === "number" || (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)))) {
    const number = Number(value);
    if (PERCENT_COLUMN_RE.test(column)) {
      const percent = Math.abs(number) <= 1 && number !== 0 ? number * 100 : number;
      return `${Number(percent.toFixed(2)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
    }
    return formatCompactNumber(number, column);
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    return JSON.stringify(value);
  }

  return String(value);
};

const escapeMarkdownTableCell = (value) =>
  String(value ?? "")
    .replace(/\|/g, "\\|")
    .replace(/\r?\n/g, "<br />")
    .trim();

export const structuredTableDataToMarkdown = (tableData) => {
  if (!Array.isArray(tableData) || tableData.length === 0) return "";

  const rows = tableData.filter(isPlainObject);
  if (!rows.length) return "";

  const columns = getStructuredTableColumns(rows);
  if (!columns.length) return "";

  const header = `| ${columns.map((column) => escapeMarkdownTableCell(formatHeaderLabel(column))).join(" | ")} |`;
  const divider = `| ${columns.map(() => "---").join(" | ")} |`;
  const body = rows.map((row) =>
    `| ${columns.map((column) => escapeMarkdownTableCell(formatStructuredValue(row[column], column))).join(" | ")} |`
  );

  return [header, divider, ...body].join("\n");
};

const getChartUnavailableMessage = (message) => {
  if (!message?.partial) return "";
  if (message?.chartStatus === "timeout") {
    return "Chart unavailable: generation timed out.";
  }
  if (message?.chartStatus === "failed") {
    return "Chart unavailable: generation failed.";
  }
  return "";
};

const buildResultSection = ({ tableContent, chart, chartPending = false, chartNotice = "", source }) => ({
  kind: "result",
  tableContent,
  chart,
  chartPending,
  chartNotice,
  defaultView: "table",
  source,
});

const pushSection = (sections, section) => {
  if (!section) return;

  if (!section.pending) {
    if (section.kind === "text" || section.kind === "table" || section.kind === "insight" || section.kind === "chartNotice") {
      if (!String(section.content || "").trim()) return;
    }
    if (section.kind === "result" && !String(section.tableContent || "").trim()) return;
    if (section.kind === "chart" && !section.chart) return;
    if (section.kind === "followUps" && !(section.followUps || []).length) return;
  }

  sections.push({
    ...section,
    id: `${section.kind}-${sections.length}`,
    order: sections.length,
  });
};

export const isTextLikeStreamSection = (section) =>
  section?.kind === "text" || section?.kind === "insight";

export const buildRenderableSections = (message, ssePhase = null) => {
  const sections = [];
  const structuredTableContent = structuredTableDataToMarkdown(message?.tableData);
  const isSqlResponse = Boolean(
    message?.sql ||
      structuredTableContent ||
      (message?.isDownloadable && message?.downloadId)
  );
  const chartUnavailableMessage = getChartUnavailableMessage(message);

  if (isSqlResponse) {
    const rawContent = message?.content || message?.sql_response || "";
    const { summaryAbove, summaryBelow, tableContent } = splitSqlResponseContent(rawContent);

    if (structuredTableContent) {
      const summaryPending = ssePhase === "table_only";
      const isTerminalPhase = ssePhase === "completed" || ssePhase === "partial_success";
      const chartPending = Boolean(ssePhase && ssePhase !== "chart_ready" && !isTerminalPhase);
      const followUpsPending = Boolean(ssePhase && !isTerminalPhase);

      pushSection(sections, { kind: "text", content: summaryAbove, pending: summaryPending });
      pushSection(sections, buildResultSection({
        tableContent: structuredTableContent,
        chart: chartUnavailableMessage ? null : message?.chart,
        chartPending: chartUnavailableMessage ? false : chartPending,
        chartNotice: chartUnavailableMessage,
        source: "tableData",
      }));
      pushSection(sections, { kind: "text", content: summaryBelow, pending: summaryPending });
      pushSection(sections, { kind: "insight", content: message?.insight });
      pushSection(sections, { kind: "followUps", followUps: message?.followUps || [], pending: followUpsPending });
    } else {
      pushSection(sections, { kind: "text", content: summaryAbove });
      pushSection(sections, buildResultSection({
        tableContent,
        chart: chartUnavailableMessage ? null : message?.chart,
        chartNotice: chartUnavailableMessage,
        source: "markdown",
      }));
      pushSection(sections, { kind: "text", content: summaryBelow });
      pushSection(sections, { kind: "insight", content: message?.insight });
      pushSection(sections, { kind: "followUps", followUps: message?.followUps || [] });
    }
    return sections;
  }

  if (!message?.insight) {
    pushSection(sections, { kind: "text", content: message?.content });
  }

  pushSection(sections, { kind: "chartNotice", content: chartUnavailableMessage });
  if (!chartUnavailableMessage) {
    pushSection(sections, { kind: "chart", chart: message?.chart });
  }
  pushSection(sections, { kind: "insight", content: message?.insight });
  pushSection(sections, { kind: "followUps", followUps: message?.followUps || [] });

  return sections;
};

export const createLiveStreamState = (message, options = {}) => {
  const allSections = buildRenderableSections(message, message?.ssePhase || null);
  const sectionKinds = Array.isArray(options.sectionKinds)
    ? options.sectionKinds
    : null;
  const sectionIds = Array.isArray(options.sectionIds)
    ? options.sectionIds
    : null;
  const sections = allSections.filter((section) => {
    if (sectionKinds && !sectionKinds.includes(section.kind)) return false;
    if (sectionIds && !sectionIds.includes(section.id)) return false;
    return true;
  });
  const targetSectionIds = sections.map((section) => section.id);

  return {
    messageId: message?.id,
    status: sections.length ? "streaming" : "completed",
    activeSectionIndex: sections.length ? 0 : -1,
    activeSectionId: sections[0]?.id || null,
    typedContentBySection: {},
    revealedSectionIds: [],
    targetSectionIds,
    completed: sections.length === 0,
    sections,
  };
};

export const chunkTextForStreaming = (content) => {
  const text = String(content || "");
  const tokens = text.match(/\S+\s*/g) || [];

  if (!tokens.length) return [];

  const chunks = [];
  let cursor = 0;
  let nextBatchSize = 2;

  while (cursor < tokens.length) {
    const batch = tokens.slice(cursor, cursor + nextBatchSize).join("");
    chunks.push(batch);
    cursor += nextBatchSize;
    nextBatchSize = nextBatchSize === 2 ? 3 : 2;
  }

  return chunks;
};

export const getStreamingChunkDelay = (chunk) => {
  const text = String(chunk || "");

  if (BLANK_LINE_RE.test(text) || SENTENCE_END_RE.test(text)) {
    return STREAM_TEXT_BATCH_INTERVAL_MS + STREAM_TEXT_SENTENCE_PAUSE_MS;
  }

  return STREAM_TEXT_BATCH_INTERVAL_MS;
};
